import type { PhotoFillTransformInput } from './types/photo-fill-transform-input'

/**
 * SVG `transform` attribute panning (and, when `coveredWidth`/`coveredHeight` aren't available,
 * zooming) the photo `<image>` on top of the shape's auto-cover frame. Returns `undefined` for
 * the identity case so callers can omit the attribute entirely.
 */
export function photoFillImageTransform({
  frameWidth,
  frameHeight,
  offsetX,
  offsetY,
  scale,
  coveredWidth,
  coveredHeight,
  flippedX,
  flippedY,
  rotation,
}: PhotoFillTransformInput): string | undefined {
  const cx = frameWidth / 2
  const cy = frameHeight / 2
  const rotationDeg = rotation || 0
  const rotatePrefix = rotationDeg ? `rotate(${rotationDeg} ${cx} ${cy}) ` : ''

  if (coveredWidth != null && coveredHeight != null) {
    const tx = cx - coveredWidth / 2 + offsetX
    const ty = cy - coveredHeight / 2 + offsetY

    if (!flippedX && !flippedY) {
      if (!rotationDeg && tx === 0 && ty === 0) {
        return undefined
      }

      return `${rotatePrefix}translate(${tx} ${ty})`
    }

    // Mirror the image content about its own center on whichever axis is flipped: placing the
    // flipped content at `tx + coveredWidth`/`ty + coveredHeight` (instead of `tx`/`ty`) keeps it
    // in the exact same footprint the unflipped image would have occupied — only its content is
    // reversed.
    const fx = flippedX ? tx + coveredWidth : tx
    const fy = flippedY ? ty + coveredHeight : ty
    const sx = flippedX ? -1 : 1
    const sy = flippedY ? -1 : 1

    return `${rotatePrefix}translate(${fx} ${fy}) scale(${sx} ${sy})`
  }

  if (!flippedX && !flippedY && !rotationDeg && offsetX === 0 && offsetY === 0 && scale === 1) {
    return undefined
  }

  const scaleArg =
    flippedX || flippedY ? `${flippedX ? -scale : scale} ${flippedY ? -scale : scale}` : `${scale}`

  return `${rotatePrefix}translate(${offsetX} ${offsetY}) translate(${cx} ${cy}) scale(${scaleArg}) translate(${-cx} ${-cy})`
}
